import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput",
    "section",
    "sectionTitleText",
    "questionItem",
    "questionText",
    "answerText",
    "quickNavLink",
    "questionsContainer",
    "answerContainer",
    "sectionChevron",
    "questionChevron",
  ]

  static values = {
    // openQuestionId: { type: String, default: "" }, // Removed: Allow multiple questions to be open
  }

  connect() {
    this.filter();
    // Ensure all answers start collapsed and chevrons are correct
    this.answerContainerTargets.forEach(answerContainer => {
      this._collapseElement(answerContainer);
    });
    this.questionChevronTargets.forEach(chevron => {
      chevron.classList.remove("rotate-180");
    });
    // Ensure all section questionsContainers start collapsed and chevrons correct
    this.questionsContainerTargets.forEach(qsContainer => {
      this._collapseElement(qsContainer, true);
    });
    this.sectionChevronTargets.forEach(chevron => {
      chevron.classList.remove("rotate-180");
    });
  }

  filter() {
    const query = this.searchInputTarget.value.toLowerCase().trim();
    let visibleSectionsInNav = new Set();

    this.sectionTargets.forEach(sectionEl => {
      const sectionId = sectionEl.dataset.sectionId;
      const sectionTitle = sectionEl.querySelector('[data-faq-target="sectionTitleText"]').textContent.toLowerCase();
      let sectionHasVisibleQuestions = false;

      const questions = sectionEl.querySelectorAll('[data-faq-target="questionItem"]');
      questions.forEach(questionEl => {
        const questionText = questionEl.querySelector('[data-faq-target="questionText"]').textContent.toLowerCase();
        const answerText = questionEl.querySelector('[data-faq-target="answerText"]').textContent.toLowerCase();
        
        const answerContainer = questionEl.querySelector('[data-faq-target="answerContainer"]');
        const questionChevron = questionEl.querySelector('[data-faq-target="questionChevron"]');

        if (query === "" || questionText.includes(query) || answerText.includes(query)) {
          questionEl.style.display = "";
          if (query !== "") sectionHasVisibleQuestions = true;
        } else {
          questionEl.style.display = "none";
          // If a question is hidden by filter, ensure its answer is also collapsed
          if (answerContainer) this._collapseElement(answerContainer);
          if (questionChevron) questionChevron.classList.remove("rotate-180");
        }
      });

      const questionsContainer = sectionEl.querySelector('[data-faq-target="questionsContainer"]');
      const sectionChevron = sectionEl.querySelector('[data-faq-target="sectionChevron"]');

      if (query === "") {
        sectionEl.style.display = "";
        visibleSectionsInNav.add(sectionId);
        sectionEl.classList.remove("opacity-50");
      } else if (sectionTitle.includes(query) || sectionHasVisibleQuestions) {
        sectionEl.style.display = "";
        visibleSectionsInNav.add(sectionId);
        sectionEl.classList.remove("opacity-50");
        if (questionsContainer && (questionsContainer.style.maxHeight === "0px" || !questionsContainer.style.maxHeight)) {
            this._expandElement(questionsContainer, true);
            if(sectionChevron) sectionChevron.classList.add("rotate-180");
        }
      } else {
        sectionEl.style.display = "none";
        if (questionsContainer) this._collapseElement(questionsContainer, true);
        if (sectionChevron) sectionChevron.classList.remove("rotate-180");
      }
    });
    
    this.quickNavLinkTargets.forEach(navLink => {
      if (query === "" || visibleSectionsInNav.has(navLink.dataset.sectionId)) {
        navLink.style.display = "";
      } else {
        navLink.style.display = "none";
      }
    });

    // _updateQuestionsUI(); // Removed
    this._updateQuickNavLinksUI();
  }

  toggleSection(event) {
    const sectionButton = event.currentTarget;
    const sectionElement = sectionButton.closest('[data-faq-target="section"]');
    if (!sectionElement) return;

    const questionsContainer = sectionElement.querySelector('[data-faq-target="questionsContainer"]');
    const sectionChevron = sectionButton.querySelector('[data-faq-target="sectionChevron"]');

    if (!questionsContainer || !sectionChevron) return;

    const isOpening = questionsContainer.style.maxHeight === "0px" || !questionsContainer.style.maxHeight;

    if (isOpening) {
      this._expandElement(questionsContainer, true);
      sectionChevron.classList.add("rotate-180");
    } else {
      this._collapseElement(questionsContainer, true);
      sectionChevron.classList.remove("rotate-180");
      // When a section is collapsed, collapse all its questions as well
      const questionItems = sectionElement.querySelectorAll('[data-faq-target="questionItem"]');
      questionItems.forEach(qItem => {
        const answerContainer = qItem.querySelector('[data-faq-target="answerContainer"]');
        const qChevron = qItem.querySelector('[data-faq-target="questionChevron"]'); // Corrected selector
        if(answerContainer) this._collapseElement(answerContainer);
        if(qChevron) qChevron.classList.remove("rotate-180");
      });
    }
    sectionElement.classList.remove("opacity-50");
  }
  
  navigateToSection(event) {
    const sectionIdToNavigate = event.currentTarget.dataset.sectionId;
    const sectionElement = this.sectionTargets.find(el => el.dataset.sectionId === sectionIdToNavigate);

    if (sectionElement) {
      const questionsContainer = sectionElement.querySelector('[data-faq-target="questionsContainer"]');
      const sectionChevron = sectionElement.querySelector('[data-faq-target="sectionChevron"]');

      if (questionsContainer && sectionChevron) {
        if (questionsContainer.style.maxHeight === "0px" || !questionsContainer.style.maxHeight) {
            this._expandElement(questionsContainer, true);
            sectionChevron.classList.add("rotate-180");
        }
        sectionElement.classList.remove("opacity-50");
      }
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  toggleQuestion(event) {
    const questionButton = event.currentTarget;
    const questionItem = questionButton.closest('[data-faq-target="questionItem"]');
    if (!questionItem || questionItem.style.display === "none") return; 

    const answerContainer = questionItem.querySelector('[data-faq-target="answerContainer"]');
    const questionChevron = questionButton.querySelector('[data-faq-target="questionChevron"]');

    if (!answerContainer || !questionChevron) return;

    const isOpening = !answerContainer.style.maxHeight || answerContainer.style.maxHeight === "0px";

    if (isOpening) {
      this._expandElement(answerContainer);
      questionChevron.classList.add("rotate-180");
    } else {
      this._collapseElement(answerContainer);
      questionChevron.classList.remove("rotate-180");
    }

    // After a question's state changes, ensure its parent section's questionsContainer adjusts its height.
    const parentSectionElement = questionItem.closest('[data-faq-target="section"]');
    if (parentSectionElement) {
      const questionsContainer = parentSectionElement.querySelector('[data-faq-target="questionsContainer"]');
      // Check if the questionsContainer is actually expanded (maxHeight not 0px and opacity is 1)
      if (questionsContainer && questionsContainer.style.maxHeight !== "0px" && questionsContainer.style.opacity === "1") {
        this._expandElement(questionsContainer, true); 
      }
    }
  }

  // _updateQuestionsUI() { // Removed as questions are toggled independently
  // }
  
  _updateQuickNavLinksUI() {
    this.quickNavLinkTargets.forEach(navLink => {
      navLink.classList.remove('bg-zinc-800', 'text-zinc-100');
      navLink.classList.add('text-zinc-400', 'hover:text-zinc-200');
    });
  }

  _expandElement(element, isSectionContainer = false) {
    if (!element || element.style.display === 'none') return;
    
    element.style.maxHeight = 'auto'; 
    const scrollHeight = element.scrollHeight;
    element.offsetHeight; 
    element.style.maxHeight = scrollHeight + "px";

    if (isSectionContainer) {
      element.style.opacity = "1";
    }
  }

  _collapseElement(element, isSectionContainer = false) {
    if (!element) return;
    element.style.maxHeight = "0px";
    if (isSectionContainer) {
      element.style.opacity = "0";
    }
  }
} 